import { useCallback, useEffect, useRef, useState } from 'react'
import { VIEWER_HEARTBEAT_INTERVAL_MS, wsRoom } from '@apiary/shared'
import { api } from '@/lib/api'
import { getWsClient } from '@/lib/ws'
import type { ViewerSessionDto } from '@/lib/dto'

/**
 * Жизненный цикл сессии камеры на стороне клиента (docs/08 §3.3).
 *
 * Хук отвечает за три вещи, которые в документе названы «обязательно ломаются,
 * если о них не подумать»:
 *
 *  1. **Утечка WebGL-контекстов.** React StrictMode в dev монтирует эффект
 *     дважды. Флаг `disposed` в замыкании гарантирует, что сессия, открытая
 *     «мёртвым» эффектом, будет закрыта, а не потеряна.
 *  2. **Сессия остаётся живой на сервере**, если закрыли ноутбук: heartbeat
 *     каждые 10 с против серверного таймаута 30 с. При `document.hidden`
 *     heartbeat продолжает идти — иначе свёрнутая вкладка теряла бы камеру
 *     каждые полминуты; поток кадров при этом гасит сам рендер (см. §3 ниже).
 *  3. **Скрытый таб продолжает мешить.** Флаг `visible` уезжает в канвас,
 *     и рендер-цикл встаёт на паузу.
 */

export type ViewerStatus = 'idle' | 'opening' | 'open' | 'error' | 'closed'

/** Сколько вкладка может быть скрытой, прежде чем камера закроется (FR-V5). */
export const HIDDEN_GRACE_MS = 10_000

export interface ViewerSessionState {
  status: ViewerStatus
  session: ViewerSessionDto | null
  error: unknown
  /** Причина, с которой сервер закрыл сессию (`viewer.closed`). */
  closeReason: string | null
  visible: boolean
  open: (viewDistance: number) => void
  close: () => void
  setViewDistance: (viewDistance: number) => void
  /**
   * Канвас смонтирован и слушает кадры — только теперь можно входить в комнату.
   * См. комментарий к эффекту подписки: раньше это стоило пользователю всего
   * мира разом.
   */
  markCanvasReady: () => void
}

export function useViewerSession(botId: string, enabled: boolean): ViewerSessionState {
  const [status, setStatus] = useState<ViewerStatus>('idle')
  const [session, setSession] = useState<ViewerSessionDto | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [closeReason, setCloseReason] = useState<string | null>(null)
  const [visible, setVisible] = useState(() => !document.hidden)
  const [requested, setRequested] = useState<number | null>(null)
  const [canvasReady, setCanvasReady] = useState(false)
  /** Актуальный sessionId для cleanup: state в замыкание эффекта не годится. */
  const activeId = useRef<string | null>(null)

  const open = useCallback((viewDistance: number) => {
    setCloseReason(null)
    setError(null)
    setRequested(viewDistance)
  }, [])

  const close = useCallback(() => {
    setRequested(null)
  }, [])

  const markCanvasReady = useCallback(() => setCanvasReady(true), [])

  // Открытие/закрытие сессии. `requested` — единственный триггер, поэтому
  // повторный вызов `open` с тем же vd не пересоздаёт сессию.
  useEffect(() => {
    if (!enabled || requested === null) {
      setStatus(previous => (previous === 'error' ? previous : 'idle'))
      return
    }
    let disposed = false
    setStatus('opening')

    const run = async (): Promise<void> => {
      try {
        const opened = await api.viewer.open(botId, requested)
        if (disposed) {
          // Эффект уже размонтирован (StrictMode): сессия открыта, но никому не
          // нужна — закрываем сразу, иначе она проживёт до таймаута heartbeat.
          await api.viewer.close(opened.sessionId).catch(() => undefined)
          return
        }
        activeId.current = opened.sessionId
        setSession(opened)
        setStatus('open')
      } catch (cause) {
        if (disposed) return
        setError(cause)
        setStatus('error')
      }
    }
    void run()

    return () => {
      disposed = true
      const sessionId = activeId.current
      activeId.current = null
      setSession(null)
      setCanvasReady(false)
      if (sessionId === null) return
      void api.viewer.close(sessionId).catch(() => undefined)
    }
  }, [botId, enabled, requested])

  /** Вход в комнату — сразу после открытия сессии, иначе сервер сочтёт её брошенной. */
  useEffect(() => {
    const sessionId = session?.sessionId
    if (sessionId === undefined) return
    const room = wsRoom.viewer(sessionId)
    const client = getWsClient()
    if (client === null) return
    let released = false
    void client.subscribe(room).catch(cause => {
      if (released) return
      setError(cause)
      setStatus('error')
    })
    return () => {
      released = true
      client.release(room)
    }
  }, [session?.sessionId])

  /**
   * Сигнал «сцена готова» — ПОСЛЕ входа в комнату и отдельно от него.
   *
   * Мир runner выгружает один раз, пачкой в полсотни колонок, и только по этому
   * сигналу. Раньше пусковым событием был сам вход в комнату — но канвас
   * приезжает ленивым чанком бандла (three.js), и на проде пачка уходила в
   * сокет раньше, чем появлялся обработчик. Дальше чанки шлются только при
   * движении бота, поэтому камера так и оставалась чёрной при «открытой»
   * сессии, живой позиции и живых сущностях.
   */
  useEffect(() => {
    const sessionId = session?.sessionId
    if (sessionId === undefined || !canvasReady) return
    getWsClient()?.send('viewer.ready', { sessionId })
  }, [session?.sessionId, canvasReady])

  // Heartbeat: 10 с против серверного таймаута 30 с (docs/08 §5).
  useEffect(() => {
    const sessionId = session?.sessionId
    if (sessionId === undefined) return
    const client = getWsClient()
    const tick = (): void => {
      client?.send('viewer.heartbeat', { sessionId })
    }
    tick()
    const timer = window.setInterval(tick, VIEWER_HEARTBEAT_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [session?.sessionId])

  // Сервер закрыл сессию сам (бот остановился, таймаут, медленный клиент).
  useEffect(() => {
    const sessionId = session?.sessionId
    if (sessionId === undefined) return
    const client = getWsClient()
    if (client === null) return
    return client.on('viewer.closed', (...args: unknown[]) => {
      const payload = args[0] as { sessionId?: string; reason?: string } | undefined
      if (payload?.sessionId !== sessionId) return
      setCloseReason(payload.reason ?? 'client-close')
      setStatus('closed')
      activeId.current = null
      setSession(null)
      setRequested(null)
    })
  }, [session?.sessionId])

  // Скрытая вкладка не должна мешить чанки (docs/08 §3.3, пункт 3).
  useEffect(() => {
    const onVisibility = (): void => setVisible(!document.hidden)
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  /**
   * FR-V5: «камера закрывается при уходе со вкладки» — 40–80 КБ/с и живая
   * `WorldView` в runner'е не должны работать на невидимый канвас.
   *
   * Не мгновенно, а через `HIDDEN_GRACE_MS`: alt-tab на две секунды не должен
   * стоить пользователю повторной загрузки полумегабайта чанков.
   */
  useEffect(() => {
    if (visible || requested === null) return
    const timer = window.setTimeout(() => {
      setRequested(null)
      setCloseReason('client-close')
      setStatus('closed')
    }, HIDDEN_GRACE_MS)
    return () => window.clearTimeout(timer)
  }, [visible, requested])

  const setViewDistance = useCallback(
    (viewDistance: number) => {
      const sessionId = session?.sessionId
      if (sessionId === undefined) {
        setRequested(viewDistance)
        return
      }
      getWsClient()?.send('viewer.setViewDistance', { sessionId, viewDistance })
      setSession(previous => (previous === null ? previous : { ...previous, viewDistance }))
    },
    [session?.sessionId]
  )

  return {
    status,
    session,
    error,
    closeReason,
    visible,
    open,
    close,
    setViewDistance,
    markCanvasReady
  }
}
